// src/components/MeetingList.js
import React, { useState, useMemo, useEffect, useCallback } from 'react'; 
import ConfirmationModal from './ConfirmationModal';
import { createNotification, markNotificationsForMeetingAsRead } from '../services/firestoreService';
import { useTranslation } from 'react-i18next';

import { getFunctions, httpsCallable } from "firebase/functions"; 

// Helper-Komponente für einen einzelnen Eintrag in der Liste
const MeetingItem = ({ meeting, isNew, unreadCount, onSelect, onEdit, onDelete, onDuplicate, onMarkAsRead, onMarkAsUnread, isInternal, setNotification }) => {
    
    const { t } = useTranslation(); 

    // Icon-Handling für Gelesen/Ungelesen
    const handleMarkToggle = async (e) => {
        e.stopPropagation(); 
        e.preventDefault();  
        
        if (isNew) {
            await onMarkAsRead(meeting.id);
            setNotification({ message: `Protokoll "${meeting.meetingTitle || 'Protokoll'}" als gelesen markiert.`, type: 'info' });
        } else {
            const notificationData = { 
                id: meeting.id, 
                title: meeting.meetingTitle || meeting.title || "Unbenanntes Protokoll", 
                companyId: meeting.companyId, 
                isExternal: meeting.isExternal || false 
            };
            
            if (!notificationData.title) {
                setNotification({ message: "Fehler: Protokolltitel fehlt.", type: "error" });
                return;
            }

            await onMarkAsUnread(notificationData);
            setNotification({ message: `Protokoll "${notificationData.title}" als ungelesen markiert.`, type: 'info' });
        }
    };
    
    // Duplizieren
    const handleDuplicateClick = (e, includeCompleted) => {
        e.stopPropagation();
        e.preventDefault();
        onDuplicate(meeting, includeCompleted);
    };

    // Zur "Ansehen" (Extern) oder "Bearbeiten" (Intern) Ansicht
    const handleSelectClick = (e) => {
        e.preventDefault(); 
        if (isInternal) {
            onEdit(meeting); 
        } else {
            onSelect(meeting);
        }
        if (isNew) {
            onMarkAsRead(meeting.id);
        }
    };
    
    // "Bearbeiten"-Icon Klick
    const handleEditClick = (e) => {
         e.stopPropagation();
         e.preventDefault();
         if (isInternal) { onEdit(meeting); }
    };
    
    // "Löschen"-Icon Klick
    const [showConfirm, setShowConfirm] = useState(false);
    const handleDeleteClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setShowConfirm(true);
    };
    const confirmDelete = async () => {
        setShowConfirm(false);
        await onDelete(meeting.id);
    };

    // === SPRACHEN AUFBEREITEN (Robust & Additiv) ===
    const languages = useMemo(() => {
        const uniqueLangs = new Set();
        
        // 1. Basis: Originalsprache (Fallback: 'de')
        uniqueLangs.add((meeting.originalLanguage || 'de').toLowerCase());

        // 2. Explizite Liste 'availableLanguages' (falls vorhanden)
        if (Array.isArray(meeting.availableLanguages)) {
            meeting.availableLanguages.forEach(l => {
                if (l) uniqueLangs.add(l.toLowerCase());
            });
        }

        // 3. Abgeschlossene Übersetzungen aus 'translationJobs'
        if (meeting.translationJobs && typeof meeting.translationJobs === 'object') {
            Object.entries(meeting.translationJobs).forEach(([lang, status]) => {
                // Wir prüfen tolerant auf 'completed'
                if (String(status).toLowerCase() === 'completed') {
                    uniqueLangs.add(lang.toLowerCase());
                }
            });
        }

        // 4. Sortieren und Formatieren (z.B. ['DE', 'EN'])
        return Array.from(uniqueLangs).map(l => l.toUpperCase()).sort();
    }, [meeting.originalLanguage, meeting.availableLanguages, meeting.translationJobs]);

    const statusClasses = meeting.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200' : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200';
    const isForecast = meeting.status === 'forecast';
    const statusText = meeting.status === 'completed' ? t('status.completed', 'Abgeschlossen') : (isForecast ? t('status.forecast', 'Forecast') : t('status.draft', 'Entwurf'));
    
    // Anzeigetitel sicherstellen
    const displayTitle = meeting.meetingTitle || meeting.title || "Unbenanntes Protokoll";

    return (
        <a 
            href="#"
            onClick={handleSelectClick} 
            className={`flex items-center justify-between p-4 mb-2 rounded-xl shadow-md transition-all duration-200 cursor-pointer 
                        ${isNew ? 'bg-white dark:bg-gray-800 ring-2 ring-blue-500/50 border-l-4 border-blue-500' : 'bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'}`}
        >
            <div className="flex-grow min-w-0">
                <p className={`font-bold truncate text-lg ${isNew ? 'text-blue-600 dark:text-blue-300' : 'text-gray-800 dark:text-white'}`}>
                    {displayTitle}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {meeting.meetingDate || "Kein Datum"}
                    {meeting.companyName && meeting.isExternal && (
                         <span className="ml-2 font-medium">({meeting.companyName})</span>
                    )}
                </p>
                
                <div className="flex flex-wrap items-center gap-3 mt-2">
                    <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${statusClasses}`}>
                        {statusText}
                    </span>
                    
                    {/* SPRACHEN ANZEIGE (OHNE WELTKUGEL) */}
                    {languages.length > 0 && (
                        <div className="flex items-center space-x-1" title="Verfügbare Sprachen">
                            {languages.map(lang => (
                                <span 
                                    key={lang} 
                                    className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-bold uppercase border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300"
                                >
                                    {lang}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-center space-x-3 flex-shrink-0 pl-4">
                {/* Markieren-Button */}
                <button
                    onClick={handleMarkToggle}
                    title={isNew ? 'Als gelesen markieren' : 'Als ungelesen markieren'}
                    className={`p-2 rounded-full transition-colors duration-200 shadow-sm relative ${isNew ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'}`}
                >
                    {isNew ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                     {isNew && unreadCount > 0 && (
                        <span className="absolute -top-2 -right-2 block h-5 w-5 rounded-full ring-2 ring-white dark:ring-gray-800 bg-red-600 flex items-center justify-center text-white text-[10px] font-bold">
                            {unreadCount}
                        </span>
                    )}
                </button>
                
                {/* Bearbeiten-Button */}
                {isInternal && isForecast && (
                     <button 
                        onClick={handleEditClick}
                        title="Bearbeiten"
                        className="p-2 bg-gray-100 dark:bg-gray-600 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                     >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                    </button>
                )}
                
                {/* Duplizieren-Menü */}
                {isInternal && (
                    <div className="relative group">
                        <button 
                             onClick={(e) => { e.stopPropagation(); e.preventDefault(); }} 
                             title="Duplizieren"
                             className="p-2 bg-gray-100 dark:bg-gray-600 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                        >
                             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7v4a1 1 0 001 1h5m-5 0a1 1 0 011-1h5m1-9v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                        </button>
                        <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform scale-95 group-hover:scale-100 origin-top-right border dark:border-gray-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400 px-3 py-2 border-b dark:border-gray-700">Duplizieren als:</p>
                            <button onClick={(e) => handleDuplicateClick(e, false)} className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                                Ohne erledigte Aufgaben
                            </button>
                            <button onClick={(e) => handleDuplicateClick(e, true)} className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                                Mit allen Aufgaben
                            </button>
                        </div>
                    </div>
                )}

                {/* Löschen-Button */}
                {isInternal && (
                    <button 
                        onClick={handleDeleteClick}
                        title="Löschen"
                        className="p-2 bg-red-100 dark:bg-red-900/50 rounded-full text-red-600 hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                )}
            </div>
            <ConfirmationModal
                isOpen={showConfirm}
                title={t('meetingList.deleteTitle', 'Protokoll löschen?')}
                message={t('meetingList.deleteMessage', 'Sind Sie sicher, dass Sie das Protokoll "{{title}}" unwiderruflich löschen möchten?', { title: meeting.meetingTitle || 'dieses Protokoll' })}
                onConfirm={confirmDelete}
                onCancel={() => setShowConfirm(false)}
                confirmText={t('meetingList.deleteConfirm', 'Ja, löschen')}
                cancelText={t('meetingList.cancel', 'Abbrechen')}
                confirmColor="bg-red-600 hover:bg-red-700"
            />
        </a>
    );
};


const MeetingList = ({ meetings, externalMeetings, onSelectMeeting, onEditMeeting, onCreateNew, onDeleteMeeting, onDuplicateMeeting, userNotifications, userId, companyId, setNotification }) => {
    
    const { t } = useTranslation(); 
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); 

    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState(null); 
    
    const searchMeetingsFn = useCallback(
        httpsCallable(getFunctions(undefined, 'europe-west1'), 'searchMeetings'), 
    []);


    // 1. ANREICHERUNG DER DATEN (Hydration)
    const allMeetings = useMemo(() => {
        const notificationsMap = new Map();
        
        (userNotifications || []).forEach(n => {
            const currentCount = notificationsMap.get(n.meetingId) || { count: 0, notifications: [] };
            currentCount.count++;
            currentCount.notifications.push(n);
            notificationsMap.set(n.meetingId, currentCount);
        });

        const combinedList = [
            ...(meetings || []).map(m => ({ ...m, isExternal: false })),
            ...(externalMeetings || []).map(m => ({ ...m, isExternal: true }))
        ];

        return combinedList.map(m => {
            const notificationInfo = notificationsMap.get(m.id) || { count: 0, notifications: [] };
            return {
                ...m,
                meetingTitle: m.meetingTitle || m.title || "Unbenanntes Protokoll",
                isNew: notificationInfo.count > 0,
                unreadCount: notificationInfo.count,
                notifications: notificationInfo.notifications
            };
        });
    }, [meetings, externalMeetings, userNotifications]);

    
    // === Suchfunktion ===
    const performSearch = useCallback(async () => {
        if (searchTerm.trim() === '') {
            setSearchResults(null); 
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        try {
            const result = await searchMeetingsFn({ query: searchTerm });
            
            const hits = (result.data && result.data.hits) || [];

            const notificationsMap = new Map();
            (userNotifications || []).forEach(n => {
                const currentCount = notificationsMap.get(n.meetingId) || { count: 0, notifications: [] };
                currentCount.count++;
                currentCount.notifications.push(n);
                notificationsMap.set(n.meetingId, currentCount);
            });

            const hydratedResults = hits.map(hit => {
                const meetingId = hit.id || hit.meetingId || hit.objectID; 
                const notificationInfo = notificationsMap.get(meetingId) || { count: 0, notifications: [] };
                
                return {
                    ...hit, 
                    id: meetingId,
                    meetingTitle: hit.title || hit.meetingTitle || "Unbenanntes Protokoll", 
                    meetingDate: hit.date || hit.meetingDate,
                    companyName: hit.companyName,
                    isExternal: hit.isExternal || false, 
                    isNew: notificationInfo.count > 0,
                    unreadCount: notificationInfo.count,
                    notifications: notificationInfo.notifications,
                    status: hit.status || 'forecast',
                    availableLanguages: hit.availableLanguages || [],
                    originalLanguage: hit.originalLanguage || 'de'
                };
            });
            
            setSearchResults(hydratedResults);

        } catch (error) {
            console.error("Fehler bei der Suche:", error);
            setNotification({ message: t('meetingList.searchError', 'Fehler bei der Suche.'), type: 'error' });
            setSearchResults([]); 
        }
        setIsSearching(false);
    }, [searchTerm, searchMeetingsFn, userNotifications, setNotification, t]); 
    
    
    useEffect(() => {
        if (searchTerm.trim() === '') {
            setSearchResults(null);
        }
    }, [searchTerm]);
    
    
    const handleSearchSubmit = (e) => {
        e.preventDefault(); 
        performSearch();
    };


    // 2. FILTERUNG UND SORTIERUNG
    const filteredAndSortedMeetings = useMemo(() => {
        
        let list = searchResults !== null ? searchResults : allMeetings;

        if (filterStatus !== 'all') {
            list = list.filter(m => m.status === filterStatus);
        }

        if (searchResults === null) {
            list.sort((a, b) => {
                if (a.status === 'forecast' && b.status === 'completed') return -1;
                if (a.status === 'completed' && b.status === 'forecast') return 1;
                
                const dateA = a.meetingDate || a.date; 
                const dateB = b.meetingDate || b.date;
                
                if (dateA && dateB) {
                    return dateB.localeCompare(dateA); 
                } else if (dateA && !dateB) {
                    return -1;
                } else if (!dateA && dateB) {
                    return 1;
                } else {
                    return 0; 
                }
            });
        }

        return list;
    }, [
        allMeetings, 
        filterStatus, 
        searchResults 
    ]);


    // 3. GRUPPIERUNG FÜR DIE ANZEIGE
    const groupedMeetings = useMemo(() => {
        const internal = filteredAndSortedMeetings.filter(m => !m.isExternal);
        const external = filteredAndSortedMeetings.filter(m => m.isExternal);
        
        const internalUnreadCount = internal.filter(m => m.isNew).length;
        const externalUnreadCount = external.filter(m => m.isNew).length;

        return { internal, external, internalUnreadCount, externalUnreadCount };
    }, [filteredAndSortedMeetings]);
    

    const handleMarkAsRead = async (meetingId) => {
        await markNotificationsForMeetingAsRead(userId, meetingId);
    };

    const handleMarkAsUnread = async (notificationData) => {
        await createNotification(userId, notificationData);
    };


    const renderList = (title, list, unreadCount, isInternal) => (
        <div className="mb-8">
            <h3 className="text-2xl font-bold mb-4 flex items-center text-gray-800 dark:text-white">
                {title}
                {unreadCount > 0 && (
                    <span className="ml-3 px-3 py-1 text-sm font-semibold rounded-full bg-red-500 text-white shadow-md">
                         {unreadCount} {t('meetingList.unread', 'neu')}
                    </span>
                )}
            </h3>
            {list.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400">
                    {searchResults === null ? t('meetingList.noMeetings', 'Keine Protokolle in dieser Ansicht gefunden.') : ''}
                </p>
            ) : (
                <div className="space-y-3">
                    {list.map(m => (
                        <MeetingItem
                            key={m.id}
                            meeting={m}
                            isNew={m.isNew}
                            unreadCount={m.unreadCount}
                            onSelect={onSelectMeeting}
                            onEdit={onEditMeeting}
                            onDelete={onDeleteMeeting}
                            onDuplicate={onDuplicateMeeting}
                            onMarkAsRead={handleMarkAsRead}
                            onMarkAsUnread={handleMarkAsUnread}
                            isInternal={isInternal}
                            setNotification={setNotification}
                        />
                    ))}
                </div>
            )}
        </div>
    );
    
    const noMeetingsMatchCriteria = filteredAndSortedMeetings.length === 0 && (searchTerm !== '' || filterStatus !== 'all');
    const hasInternalMeetings = groupedMeetings.internal.length > 0;
    const hasExternalMeetings = groupedMeetings.external.length > 0;


    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">{t('meetingList.title', 'Alle Protokolle')}</h2>
                <button 
                    onClick={onCreateNew} 
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-colors flex items-center"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                    {t('meetingList.newMeeting', 'Neues Protokoll')}
                </button>
            </div>

            <form 
                onSubmit={handleSearchSubmit} 
                className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg flex flex-col sm:flex-row gap-4"
            >
                <div className="flex-grow relative">
                    <input
                        type="text"
                        placeholder={t('meetingList.searchPlaceholderFull', 'Volltextsuche...')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500 pr-10" 
                    />
                    {isSearching && (
                        <div className="absolute right-3 top-0 h-full flex items-center">
                            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    )}
                </div>

                <button 
                    type="submit" 
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition-colors flex items-center justify-center sm:w-auto"
                    disabled={isSearching}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span className="ml-2 sm:hidden">{t('meetingList.search', 'Suchen')}</span>
                </button>
                
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full sm:w-48 p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                >
                    <option value="all">{t('meetingList.filterAll', 'Alle')}</option>
                    <option value="forecast">{t('meetingList.filterForecast', 'Forecast')}</option>
                    <option value="completed">{t('meetingList.filterCompleted', 'Abgeschlossen')}</option>
                </select>
            </form>

            {hasInternalMeetings && renderList(t('meetingList.internalMeetings', 'Interne Protokolle'), groupedMeetings.internal, groupedMeetings.internalUnreadCount, true)}
            {hasExternalMeetings && renderList(t('meetingList.externalMeetings', 'Externe Protokolle'), groupedMeetings.external, groupedMeetings.externalUnreadCount, false)}
            
            {noMeetingsMatchCriteria && !isSearching && (
                <div className="text-center p-8 bg-gray-100 dark:bg-gray-800 rounded-xl shadow-inner mt-8">
                    {searchTerm !== '' ? (
                        <p className="text-lg text-gray-600 dark:text-gray-400">{t('meetingList.noSearchResults', 'Keine Protokolle für "{{searchTerm}}" gefunden.', { searchTerm: searchTerm })}</p>
                    ) : (
                        <p className="text-lg text-gray-600 dark:text-gray-400">{t('meetingList.noMeetingsCriteria', 'Keine Protokolle gefunden, die Ihren Kriterien entsprechen.')}</p>
                    )}
                </div>
            )}
            
            {!hasInternalMeetings && !hasExternalMeetings && !noMeetingsMatchCriteria && !isSearching && (
                 <div className="text-center p-8 bg-gray-100 dark:bg-gray-800 rounded-xl shadow-inner mt-8">
                    <p className="text-lg text-gray-600 dark:text-gray-400">{t('meetingList.noMeetings', 'Keine Protokolle in dieser Ansicht gefunden.')}</p>
                </div>
            )}
        </div>
    );
};

export default MeetingList;